import type {
  CreateKeyPersonParams,
  DuplicateKeyPersonMatch,
} from '@commenda/api';
import {
  useCreateKeyPerson,
  useGetAttributeProperties,
  useUpdateKeyPerson,
} from '@commenda/api';
import {
  DatePicker,
  FieldWrapper,
  Input,
  Label,
  MultiSelect,
  SingleCombobox,
} from '@commenda/kakashi';
import { Gender, IKeyPersonDetail } from '@commenda/types';
import {
  formatPersonName,
  getAllCountryOptions,
  isValidCalendarDate,
} from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import AddressGroupRHF from '../../components/AddressGroupRHF';
import { useNotification } from '../../hooks/useNotification';
import { DuplicateKeyPersonDialog } from './Modals/DuplicateKeyPersonDialog';
import {
  CommittedField,
  CustomFieldsBuilder,
  customFieldDraftsToParams,
  customFieldsHaveMissingRequired,
  valueIsEmpty,
  type BuilderFieldType,
  type CustomFieldDraft,
} from './CustomFieldsBuilder';

// Person-level attribute that holds a key person's custom fields (mirrors the backend).
const PROFILE_ATTRIBUTE_KEY = 'KEY_PERSON_PROFILE';

const BUILDER_FIELD_TYPES = [
  'TEXT',
  'URL',
  'EMAIL',
  'DATE',
  'PASSWORD',
  'FILE',
];
const toBuilderFieldType = (type: string): BuilderFieldType =>
  BUILDER_FIELD_TYPES.includes(type) ? (type as BuilderFieldType) : 'TEXT';

// Address is optional on this form (only firstName is required) and the backend accepts a
// partial address, so every address field is optional here.
const addressSchema = z.object({
  country: z.string().optional(),
  addressLine1: z.string().trim().optional(),
  addressLine2: z.string().trim().optional().nullable(),
  addressLine3: z.string().trim().optional().nullable(),
  state: z.string().trim().optional().nullable(),
  city: z.string().trim().optional(),
  postalCode: z.string().trim().optional(),
});

const schema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().optional(),
  dateOfBirth: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || isValidCalendarDate(v), 'Enter a valid date'),
  gender: z.string().optional(),
  nationalities: z.array(z.string()).min(1, 'Select at least one nationality'),
  countryOfCitizenship: z.string().optional(),
  countryOfResidence: z.string().optional(),
  email: z
    .string()
    .trim()
    .email('Enter a valid email')
    .optional()
    .or(z.literal('')),
  phone: z.string().trim().optional(),
  residentialAddress: addressSchema.optional(),
});

type FormFields = z.infer<typeof schema>;

const COUNTRY_OPTIONS = getAllCountryOptions().map((country) => ({
  value: country.code,
  label: country.name,
}));

const EMPTY_ADDRESS = {
  country: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  state: '',
  city: '',
  postalCode: '',
};

// Stored gender may be any case (legacy "male"/"female" vs the "Male"/"Female"/"Other" enum);
// match case-insensitively so the select prefills instead of falling back to the placeholder.
const toGenderValue = (raw?: string | null): string =>
  raw
    ? (Object.values(Gender).find(
        (g) => g.toLowerCase() === raw.toLowerCase()
      ) ?? '')
    : '';

const toDefaults = (keyPerson?: IKeyPersonDetail): FormFields => ({
  firstName: keyPerson?.firstName ?? '',
  lastName: keyPerson?.lastName ?? '',
  dateOfBirth: keyPerson?.dateOfBirth?.slice(0, 10) ?? '',
  gender: toGenderValue(keyPerson?.gender),
  nationalities: keyPerson?.nationalities ?? [],
  countryOfCitizenship: keyPerson?.countryOfCitizenship ?? '',
  countryOfResidence: keyPerson?.countryOfResidence ?? '',
  email: keyPerson?.primaryEmail ?? '',
  phone: keyPerson?.primaryPhone ?? '',
  residentialAddress: keyPerson?.residentialAddress
    ? {
        country: keyPerson.residentialAddress.country ?? '',
        addressLine1: keyPerson.residentialAddress.addressLine1 ?? '',
        addressLine2: keyPerson.residentialAddress.addressLine2 ?? '',
        addressLine3: keyPerson.residentialAddress.addressLine3 ?? '',
        state: keyPerson.residentialAddress.state ?? '',
        city: keyPerson.residentialAddress.city ?? '',
        postalCode: keyPerson.residentialAddress.postalCode ?? '',
      }
    : { ...EMPTY_ADDRESS },
});

// Existing registered custom-field values, keyed by the property's unqualified key.
const toCustomValues = (
  keyPerson?: IKeyPersonDetail
): Record<string, unknown> =>
  keyPerson
    ? Object.fromEntries(keyPerson.customFields.map((f) => [f.key, f.value]))
    : {};

// Trims blank fields; returns undefined when the whole address is empty so we never write
// an all-blank address.
const toAddressPayload = (address?: FormFields['residentialAddress']) => {
  if (!address) return undefined;
  const clean = (value?: string | null) =>
    value && value.trim().length > 0 ? value.trim() : undefined;
  const payload = {
    country: clean(address.country),
    addressLine1: clean(address.addressLine1),
    addressLine2: clean(address.addressLine2),
    addressLine3: clean(address.addressLine3),
    state: clean(address.state),
    city: clean(address.city),
    postalCode: clean(address.postalCode),
  };
  return Object.values(payload).some((value) => value !== undefined)
    ? payload
    : undefined;
};

/**
 * The key-person profile form (identity + residential address + custom fields), with no chrome.
 * Renders a `<form id={formId}>` so a submit button anywhere (the modal footer, a wizard step)
 * can drive it via `form={formId}`. On submit it creates/updates the person and calls `onSaved`.
 * Callers own the heading, layout width, and footer; `onStateChange` lets them gate their button.
 */
export const KeyPersonForm = ({
  companyId,
  keyPerson,
  formId,
  onSaved,
  onStateChange,
}: {
  companyId: number;
  /** Present → edit mode: the form is prefilled and submits a PATCH instead of a create. */
  keyPerson?: IKeyPersonDetail;
  formId: string;
  onSaved: (keyPersonId: number, displayName: string) => void;
  onStateChange?: (state: { isSaving: boolean; canSubmit: boolean }) => void;
}) => {
  const isEditing = keyPerson !== undefined;
  const { setNotification } = useNotification();
  const createMutation = useCreateKeyPerson();
  // id is only read at submit time, so 0 is a harmless placeholder in add mode.
  const updateMutation = useUpdateKeyPerson(keyPerson?.id ?? 0, companyId);
  const { data: registry } = useGetAttributeProperties(companyId);

  // Company-wide custom properties on the person profile — surfaced so existing values can
  // be edited (and required ones filled). Values flow back through `customFields`.
  const profileCustomProps = useMemo(() => {
    const attribute = registry?.attributes.find(
      (a) => a.key === PROFILE_ATTRIBUTE_KEY
    );
    return (attribute?.properties ?? []).filter(
      // Only surface types the editor renders correctly. ENUM/NUMBER/BOOLEAN/ADDRESS would
      // render as a blank text input and, once edited, fail the whole submit with TYPE_MISMATCH;
      // their stored values still round-trip untouched via customValues.
      (p) => p.isCustom && !p.isOneOff && BUILDER_FIELD_TYPES.includes(p.type)
    );
  }, [registry]);

  const [duplicatePrompt, setDuplicatePrompt] = useState<{
    matches: DuplicateKeyPersonMatch[];
    payload: CreateKeyPersonParams;
    displayName: string;
  } | null>(null);

  // Locally-added (not-yet-registered) fields; existing values live in customValues.
  const [fieldDrafts, setFieldDrafts] = useState<CustomFieldDraft[]>([]);
  const [customValues, setCustomValues] = useState<Record<string, unknown>>(
    () => toCustomValues(keyPerson)
  );

  const methods = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: toDefaults(keyPerson),
  });
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = methods;

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const missingRequiredCustom = profileCustomProps.some(
    (p) => p.isRequired && valueIsEmpty(p.type, customValues[p.unqualifiedKey])
  );
  // In edit mode, keep the CTA disabled until something actually changes. RHF's isDirty
  // covers the form fields; customValues (surfaced fields) and fieldDrafts are tracked apart.
  const initialCustomValuesKey = useMemo(
    () => JSON.stringify(toCustomValues(keyPerson)),
    [keyPerson]
  );
  const hasChanges =
    isDirty ||
    fieldDrafts.length > 0 ||
    JSON.stringify(customValues) !== initialCustomValuesKey;

  const canSubmit =
    !isSaving &&
    !missingRequiredCustom &&
    !customFieldsHaveMissingRequired(fieldDrafts) &&
    (!isEditing || hasChanges);

  useEffect(() => {
    onStateChange?.({ isSaving, canSubmit });
  }, [isSaving, canSubmit, onStateChange]);

  const onSubmit = async (data: FormFields) => {
    const customFieldValues = Object.fromEntries(
      Object.entries(customValues).filter(
        ([, value]) => value !== undefined && value !== ''
      )
    );
    const newCustomFields = customFieldDraftsToParams(fieldDrafts);
    const shared = {
      firstName: data.firstName,
      nationalities: data.nationalities ?? [],
      residentialAddress: toAddressPayload(data.residentialAddress),
      ...(Object.keys(customFieldValues).length
        ? { customFields: customFieldValues }
        : {}),
      ...(newCustomFields.length ? { newCustomFields } : {}),
    };
    const createPayload: CreateKeyPersonParams = {
      companyId,
      ...shared,
      lastName: data.lastName || undefined,
      dateOfBirth: data.dateOfBirth || undefined,
      gender: data.gender || undefined,
      countryOfCitizenship: data.countryOfCitizenship || undefined,
      countryOfResidence: data.countryOfResidence || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
    };
    try {
      if (isEditing) {
        // On edit, an emptied optional field is sent as null to clear it; on create we omit it.
        const updated = await updateMutation.mutateAsync({
          ...shared,
          lastName: data.lastName || null,
          dateOfBirth: data.dateOfBirth || null,
          gender: data.gender || null,
          countryOfCitizenship: data.countryOfCitizenship || null,
          countryOfResidence: data.countryOfResidence || null,
          email: data.email || null,
          phone: data.phone || null,
        });
        setNotification({
          show: true,
          valid: true,
          title: 'Key person updated',
          message: 'The changes were saved.',
        });
        setFieldDrafts([]);
        onSaved(updated.id, formatPersonName(data));
        return;
      }

      const created = await createMutation.mutateAsync(createPayload);
      if (created.status === 'duplicate') {
        setDuplicatePrompt({
          matches: created.matches,
          payload: createPayload,
          displayName: formatPersonName(data),
        });
        return;
      }
      setNotification({
        show: true,
        valid: true,
        title: 'Key person added',
        message: 'The key person was created.',
      });
      setFieldDrafts([]);
      onSaved(created.person.id, formatPersonName(data));
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: isEditing
          ? 'Failed to update key person'
          : 'Failed to add key person',
        message: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const createDistinctPerson = async () => {
    if (duplicatePrompt === null) return;
    try {
      const saved = await createMutation.mutateAsync({
        ...duplicatePrompt.payload,
        confirmDistinctPerson: true,
      });
      // confirmDistinctPerson suppresses the refusal, so this create always lands.
      if (saved.status === 'duplicate') return;
      setNotification({
        show: true,
        valid: true,
        title: 'Key person added',
        message: 'The key person was created.',
      });
      setFieldDrafts([]);
      const { displayName } = duplicatePrompt;
      setDuplicatePrompt(null);
      onSaved(saved.person.id, displayName);
    } catch (error) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to add key person',
        message: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-3xl"
      >
        <div className="flex flex-col gap-3xl rounded-md border-[0.5px] border-border-primary bg-bg-primary p-3xl">
          <div className="flex flex-col gap-2xl">
            <div className="flex gap-2xl">
              <div className="flex-1">
                <FieldWrapper>
                  <Label required>First name</Label>
                  <Input
                    {...register('firstName')}
                    placeholder="First name"
                    error={errors.firstName?.message}
                  />
                </FieldWrapper>
              </div>
              <div className="flex-1">
                <FieldWrapper>
                  <Label optional>Last name</Label>
                  <Input {...register('lastName')} placeholder="Last name" />
                </FieldWrapper>
              </div>
            </div>
            <FieldWrapper>
              <Label optional>Date of birth</Label>
              <DatePicker
                date={watch('dateOfBirth') || undefined}
                setDate={(date) =>
                  setValue('dateOfBirth', date || '', { shouldDirty: true })
                }
                placeholder="Select date"
              />
              {errors.dateOfBirth && (
                <p className="txt-extra-small-regular text-text-error">
                  {errors.dateOfBirth.message}
                </p>
              )}
            </FieldWrapper>
            <FieldWrapper>
              <Label optional>Gender</Label>
              <SingleCombobox
                options={Object.values(Gender).map((value) => ({
                  value,
                  label: value,
                }))}
                value={watch('gender') || ''}
                setValue={(value) =>
                  setValue('gender', value, { shouldDirty: true })
                }
                placeholder="Select gender"
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label required>Nationalities</Label>
              <MultiSelect
                options={COUNTRY_OPTIONS}
                value={watch('nationalities') ?? []}
                defaultValue={watch('nationalities') ?? []}
                onValueChange={(value: string[]) =>
                  setValue('nationalities', value, { shouldDirty: true })
                }
              />
              {errors.nationalities && (
                <p className="txt-extra-small-regular text-text-error">
                  {errors.nationalities.message}
                </p>
              )}
            </FieldWrapper>
            <FieldWrapper>
              <Label optional>Country of citizenship</Label>
              <SingleCombobox
                options={COUNTRY_OPTIONS}
                value={watch('countryOfCitizenship') || ''}
                setValue={(value) =>
                  setValue('countryOfCitizenship', value, { shouldDirty: true })
                }
                placeholder="Select country of citizenship"
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label optional>Country of residence</Label>
              <SingleCombobox
                options={COUNTRY_OPTIONS}
                value={watch('countryOfResidence') || ''}
                setValue={(value) =>
                  setValue('countryOfResidence', value, { shouldDirty: true })
                }
                placeholder="Select country of residence"
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label optional>Primary email</Label>
              <Input
                {...register('email')}
                placeholder="name@example.com"
                error={errors.email?.message}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Label optional>Primary phone</Label>
              <Input {...register('phone')} placeholder="Phone number" />
            </FieldWrapper>
          </div>

          <div className="flex flex-col gap-2xl">
            <p className="txt-medium-bold text-text-primary">
              Residential address
            </p>
            <AddressGroupRHF
              pathPrefix="residentialAddress"
              statePlacement="suffix"
              optional
            />
          </div>
        </div>

        {profileCustomProps.map((prop) => (
          <CommittedField
            key={prop.id}
            field={{
              id: prop.id,
              type: toBuilderFieldType(prop.type),
              label: prop.label,
              isRequired: prop.isRequired,
              saveForFuture: true,
              value: customValues[prop.unqualifiedKey],
            }}
            companyId={companyId}
            onChange={(patch) =>
              setCustomValues((prev) => ({
                ...prev,
                [prop.unqualifiedKey]: patch.value,
              }))
            }
          />
        ))}

        <CustomFieldsBuilder
          fields={fieldDrafts}
          onChange={setFieldDrafts}
          companyId={companyId}
        />
      </form>

      <DuplicateKeyPersonDialog
        isOpen={duplicatePrompt !== null}
        onClose={() => setDuplicatePrompt(null)}
        name={duplicatePrompt?.displayName ?? ''}
        matches={duplicatePrompt?.matches ?? []}
        isCreating={createMutation.isPending}
        onUseExisting={(match) => {
          setDuplicatePrompt(null);
          onSaved(match.id, match.name);
        }}
        onCreateAnyway={createDistinctPerson}
      />
    </FormProvider>
  );
};
